import { CharacterHelpers, LocalCharacterInitializer } from "../../../../../entities/characters/Character";
import { OwnedSkillReference } from "../../../../../entities/Ownership";
import { ErrorToast } from "../../../../../entities/toasts/Toasts";
import { TimedThrottle } from "../../../../ConcurrencyUtilities";
import { ApiDefaultState } from "../../../api/ApiStore.State";
import { AppThunkAction } from "../../../ApplicationState";
import { ToastAction, ToastDispatchables } from "../../../toasts/Toasts.Actions";
import { CharacterUpdateHealth, CharacterSetJuiced } from "./CharacterPropertyActions";

export type CharacterSaveSkill = { type: 'CHARACTER_SAVE_SKILL', characterId: string, skillId: string }
export type CharacterUpdateSkill = { type: 'CHARACTER_UPDATE_SKILL', characterId: string, skillId: string, updateBy: number };
export type CharacterAdjustSkill = { type: 'CHARACTER_ADJUST_SKILL', characterId: string, skillId: string, adjustment: number };
export type CharacterOverrideSkill = { type: 'CHARACTER_OVERRIDE_SKILL', characterId: string, skillId: string, override?: number };

export type CharacterSkillAction = CharacterSaveSkill
    | CharacterUpdateSkill
    | CharacterAdjustSkill
    | CharacterOverrideSkill;

const throttleTimer = 500;
const SkillUpdateThrottles: {
    [key: string]: TimedThrottle
} = {};

export const CharacterSkillActions = {
    updateSkill: (character: LocalCharacterInitializer, reference: OwnedSkillReference, updateBy: number): AppThunkAction<CharacterSkillAction | CharacterUpdateHealth | CharacterSetJuiced | ToastAction> =>
        async (dispatch, getState) => {
            SkillUpdateThrottles[reference.id] = SkillUpdateThrottles[reference.id] ?? new TimedThrottle(throttleTimer);

            const oldVersion = { ...reference };

            const oldHealth = character.currentHealth;
            const oldHealthRatio = character.currentHealth / CharacterHelpers.getMaxHealth(character);

            const undo = () => {
                dispatch({ type: 'CHARACTER_UPDATE_SKILL', characterId: character.id, skillId: reference.id, updateBy: updateBy * -1 });
                dispatch({ type: 'CHARACTER_UPDATE_HEALTH', characterId: character.id, currentHealth: oldHealth });
                const skill = getState().library.skills.find(s => s.id == reference.id);
                ToastDispatchables.toastLegacy(new ErrorToast(`Error updating ${skill?.name ?? "unknown skill"}.`), dispatch);
            }

            dispatch({ type: 'CHARACTER_UPDATE_SKILL', characterId: character.id, skillId: reference.id, updateBy });
            
            const copyCharacter = { ...character, skills: [...character.skills.filter(s => s.id != reference.id), { ...reference, level: reference.level + updateBy }] };
            const newHealth = Math.round(CharacterHelpers.getMaxHealth(copyCharacter) * oldHealthRatio);
            const oldCharacterVersion = { ...character };
            const newCharacterVersion = { ...character, currentHealth: newHealth };
            dispatch({ type: 'CHARACTER_UPDATE_HEALTH', characterId: character.id, currentHealth: newHealth });

            try {
                const callback = async () => {
                    const patchState = getState();
                    const patchedCharacter = patchState.character.characters.find(c => c.id == character.id)
                    if (patchedCharacter == null) {
                        return;
                    }

                    const newSkillVersion = patchedCharacter.skills.first(s => s.id == reference.id);
                    if (newSkillVersion == null) {
                        return;
                    }

                    const [skillResponse, characterResponse] = await Promise.all([
                        ApiDefaultState.characters.updateSkill(character.id, oldVersion, newSkillVersion),
                        ApiDefaultState.characters.patchCharacter(oldCharacterVersion, newCharacterVersion)
                    ]);
                    if (skillResponse.status == 'Error') {
                        undo();
                        ToastDispatchables.toastValidationResults(skillResponse.validationResults, dispatch);
                    }
                    else if (characterResponse.status == 'Error') {
                        undo();
                        ToastDispatchables.toastValidationResults(characterResponse.validationResults, dispatch);
                    }
                }

                await SkillUpdateThrottles[reference.id]!.scheduleProcess(callback);
            }
            catch (e) {
                undo();
                console.error(e);
            }
        },

    adjustSkill: (character: LocalCharacterInitializer, reference: OwnedSkillReference, adjustment: number): AppThunkAction<CharacterSkillAction | CharacterUpdateHealth | CharacterSetJuiced | ToastAction> =>
        async (dispatch, getState) => {
            const oldVersion = { ...reference };
            const newVersion = { ...reference, adjustment };

            const oldHealth = character.currentHealth;
            const oldHealthRatio = character.currentHealth / CharacterHelpers.getMaxHealth(character);

            const undo = () => {
                dispatch({ type: 'CHARACTER_ADJUST_SKILL', characterId: character.id, skillId: reference.id, adjustment: oldVersion.adjustment });
                dispatch({ type: 'CHARACTER_UPDATE_HEALTH', characterId: character.id, currentHealth: oldHealth });
                const skill = getState().library.skills.find(s => s.id == reference.id);
                ToastDispatchables.toastLegacy(new ErrorToast(`Error adjusting ${skill?.name ?? "unknown skill"}.`), dispatch);
            }
            
            dispatch({ type: 'CHARACTER_ADJUST_SKILL', characterId: character.id, skillId: reference.id, adjustment: newVersion.adjustment });
            
            const newHealth = Math.round(CharacterHelpers.getMaxHealth(character) * oldHealthRatio);
            const oldCharacterVersion = { ...character };
            const newCharacterVersion = { ...character, currentHealth: newHealth };
            dispatch({ type: 'CHARACTER_UPDATE_HEALTH', characterId: character.id, currentHealth: newHealth });

            try {
                const [skillResponse, characterResponse] = await Promise.all([
                    ApiDefaultState.characters.updateSkill(character.id, oldVersion, newVersion),
                    ApiDefaultState.characters.patchCharacter(oldCharacterVersion, newCharacterVersion)
                ]);
                if (skillResponse.status == 'Error') {
                    undo();
                    ToastDispatchables.toastValidationResults(skillResponse.validationResults, dispatch);
                }
                else if (characterResponse.status == 'Error') {
                    undo();
                    ToastDispatchables.toastValidationResults(characterResponse.validationResults, dispatch);
                }
            }
            catch (e) {
                undo();
                console.error(e);
            }
        },

    overrideSkill: (character: LocalCharacterInitializer, reference: OwnedSkillReference, override: number | undefined): AppThunkAction<CharacterSkillAction | CharacterUpdateHealth | CharacterSetJuiced | ToastAction> =>
        async (dispatch, getState) => {
            const oldVersion = { ...reference };
            const newVersion = { ...reference, override };

            const oldHealth = character.currentHealth;
            const oldHealthRatio = character.currentHealth / CharacterHelpers.getMaxHealth(character);

            const undo = () => {
                dispatch({ type: 'CHARACTER_OVERRIDE_SKILL', characterId: character.id, skillId: reference.id, override: reference.override });
                dispatch({ type: 'CHARACTER_UPDATE_HEALTH', characterId: character.id, currentHealth: oldHealth });
                const skill = getState().library.skills.find(s => s.id == reference.id);
                ToastDispatchables.toastLegacy(new ErrorToast(`Server error overriding ${skill?.name ?? "unknown skill"}.`), dispatch);
            }

            dispatch({ type: 'CHARACTER_OVERRIDE_SKILL', characterId: character.id, skillId: reference.id, override });

            const newHealth = Math.round(CharacterHelpers.getMaxHealth(character) * oldHealthRatio);
            const oldCharacterVersion = { ...character };
            const newCharacterVersion = { ...character, currentHealth: newHealth };
            dispatch({ type: 'CHARACTER_UPDATE_HEALTH', characterId: character.id, currentHealth: newHealth });

            try {
                const [skillResponse, characterResponse] = await Promise.all([
                    ApiDefaultState.characters.updateSkill(character.id, oldVersion, newVersion),
                    ApiDefaultState.characters.patchCharacter(oldCharacterVersion, newCharacterVersion)
                ]);
                if (skillResponse.status == 'Error') {
                    undo();
                    ToastDispatchables.toastValidationResults(skillResponse.validationResults, dispatch);
                }
                else if (characterResponse.status == 'Error') {
                    undo();
                    ToastDispatchables.toastValidationResults(characterResponse.validationResults, dispatch);
                }
            }
            catch (e) {
                undo();
                console.error(e);
            }
        }
}